
export interface ACMGCriterion {
  code: string;
  category: 'Pathogenic' | 'Benign';
  strength: 'Very Strong' | 'Strong' | 'Moderate' | 'Supporting';
  description: string;
  details: string;
  automated: boolean;
  requirements?: string[];
}

export const acmgCriteria: ACMGCriterion[] = [
  {
    code: 'PVS1',
    category: 'Pathogenic',
    strength: 'Very Strong',
    description: 'Null variant in a gene where LOF is a known mechanism of disease',
    details: 'Nonsense, frameshift, canonical splice sites, initiation codon, single or multi-exon deletion',
    automated: true,
  },
  {
    code: 'PS1',
    category: 'Pathogenic',
    strength: 'Strong',
    description: 'Same amino acid change as a previously established pathogenic variant',
    details: 'Different nucleotide change causing same amino acid change as known pathogenic variant',
    automated: false,
  },
  {
    code: 'PS2',
    category: 'Pathogenic',
    strength: 'Strong',
    description: 'De novo (both maternity and paternity confirmed)',
    details: 'Proven de novo mutation in a patient with the disease and no family history',
    automated: false,
  },
  {
    code: 'PS3',
    category: 'Pathogenic',
    strength: 'Strong',
    description: 'Functional studies support a damaging effect',
    details: 'Well-established functional studies show a deleterious effect on gene/gene product',
    automated: false,
  },
  {
    code: 'PS4',
    category: 'Pathogenic',
    strength: 'Strong',
    description: 'Increased prevalence in affected individuals compared to controls',
    details: 'Variant significantly more prevalent in affected individuals versus controls',
    automated: false,
  },
  {
    code: 'PM1',
    category: 'Pathogenic',
    strength: 'Moderate',
    description: 'Located in a mutational hot spot or critical functional domain',
    details: 'Located in a mutational hot spot and/or critical functional domain without benign variation',
    automated: false,
  },
  {
    code: 'PM2',
    category: 'Pathogenic',
    strength: 'Moderate',
    description: 'Absent or extremely rare in population databases',
    details: `Absent from controls or at extremely low frequency if recessive.`,
    automated: true,
  },
  {
    code: 'PM3',
    category: 'Pathogenic',
    strength: 'Moderate',
    description: 'Detected in trans with a pathogenic variant for recessive disorders',
    details: 'For recessive disorders, detected in trans with a pathogenic variant',
    automated: false,
  },
  {
    code: 'PM4',
    category: 'Pathogenic',
    strength: 'Moderate',
    description: 'Protein length changes due to in-frame variants',
    details: 'Protein length changes due to in-frame deletions/insertions or stop-loss variants',
    automated: false,
  },
  {
    code: 'PM5',
    category: 'Pathogenic',
    strength: 'Moderate',
    description: 'Novel missense change at residue previously seen with a pathogenic missense',
    details: 'Novel missense change at an amino acid residue where a different pathogenic missense change has been seen before',
    automated: false,
  },
  {
    code: 'PM6',
    category: 'Pathogenic',
    strength: 'Moderate',
    description: 'Assumed de novo without full parental confirmation',
    details: 'Assumed de novo, but without confirmation of paternity and maternity',
    automated: false,
  },
  {
    code: 'PP1',
    category: 'Pathogenic',
    strength: 'Supporting',
    description: 'Co-segregation with disease in affected family members',
    details: 'Co-segregation with disease in multiple affected family members',
    automated: false,
  },
  {
    code: 'PP2',
    category: 'Pathogenic',
    strength: 'Supporting',
    description: 'Gene with low rate of benign missense variation',
    details: 'Missense variant in a gene that has a low rate of benign missense variation',
    automated: false,
  },
  {
    code: 'PP3',
    category: 'Pathogenic',
    strength: 'Supporting',
    description: 'Computational evidence suggests a deleterious effect',
    details: 'Multiple lines of computational evidence support a deleterious effect',
    automated: false,
  },
  {
    code: 'PP4',
    category: 'Pathogenic',
    strength: 'Supporting',
    description: 'Highly specific phenotype/family history for a single genetic etiology',
    details: 'Patient\'s phenotype or family history is highly specific for a disease with a single genetic etiology',
    automated: false,
  },
  {
    code: 'PP5',
    category: 'Pathogenic',
    strength: 'Supporting',
    description: 'Reputable source reports as pathogenic, no independent evaluation available',
    details: 'Reputable source recently reports variant as pathogenic but evidence is not available',
    automated: true, 
  },
  {
    code: 'BA1',
    category: 'Benign',
    strength: 'Very Strong',
    description: 'High allele frequency (>5%) in multiple large databases',
    details: 'Allele frequency is >5% in Exome Sequencing Project, 1000 Genomes, or ExAC',
    automated: true,
  },
  {
    code: 'BS1',
    category: 'Benign',
    strength: 'Strong',
    description: 'Allele frequency greater than expected for the disorder',
    details: 'Allele frequency is greater than expected for disorder',
    automated: true,
  },
  {
    code: 'BS2',
    category: 'Benign',
    strength: 'Strong',
    description: 'Observed in healthy adults despite full penetrance at early age',
    details: 'Observed in a healthy adult individual for a dominant or early-onset condition',
    automated: false,
  },
  {
    code: 'BS3',
    category: 'Benign',
    strength: 'Strong',
    description: 'Functional studies show no damaging effect',
    details: 'Well-established functional studies show no deleterious effect',
    automated: true,
  },
  {
    code: 'BS4',
    category: 'Benign',
    strength: 'Strong',
    description: 'Lack of segregation in affected family members',
    details: 'Lack of segregation in affected members of a family',
    automated: false,
  },
  {
    code: 'BP1',
    category: 'Benign',
    strength: 'Supporting',
    description: 'Missense variant in a gene primarily causing disease via truncation',
    details: 'Missense variant in a gene for which primarily truncating variants are known to cause disease',
    automated: false,
  },
  {
    code: 'BP2',
    category: 'Benign',
    strength: 'Supporting',
    description: 'Observed in trans/cis with a pathogenic variant under certain conditions',
    details: 'Observed in trans with a pathogenic variant for a fully penetrant dominant gene/disorder or observed in cis with a pathogenic variant in any inheritance pattern',
    automated: false,
  },
  {
    code: 'BP3',
    category: 'Benign',
    strength: 'Supporting',
    description: 'In-frame changes in a repetitive region without known function',
    details: 'In-frame deletions/insertions in a repetitive region without a known function',
    automated: false,
  },
  {
    code: 'BP4',
    category: 'Benign',
    strength: 'Supporting',
    description: 'Computational evidence suggests no impact on function',
    details: 'Multiple lines of computational evidence suggest no impact on gene/gene product',
    automated: false,
  },
  {
    code: 'BP5',
    category: 'Benign',
    strength: 'Supporting',
    description: 'Variant found in a case with an alternate molecular basis',
    details: 'Variant found in a case with an alternate molecular cause of disease',
    automated: false,
  },
  {
    code: 'BP6',
    category: 'Benign',
    strength: 'Supporting',
    description: 'Reputable source reports variant as benign, no independent evaluation available',
    details: 'Reputable source recently reports variant as benign but evidence is not available',
    automated: false,
  },
  {
    code: 'BP7',
    category: 'Benign',
    strength: 'Supporting',
    description: 'Synonymous variant predicted to have no splicing impact',
    details: 'A synonymous variant for which splicing prediction algorithms predict no impact to the splice consensus sequence nor the creation of a new splice site AND the nucleotide is not highly conserved',
    automated: true,
  },
]; 